import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";
import type {
  LifecyclePipeline,
  OpportunityRow,
  SavedSegment,
  SegmentHousehold,
  SegmentSummary,
  SuppressedItem,
  Task,
} from "./types";

interface Page<T> {
  data: T[];
  nextCursor: string | null;
  hasMore: boolean;
}

/** GET /v1/crm/lifecycle — the caller's book bucketed into lifecycle stages (CR-5). */
export function useLifecycle() {
  const api = useApi();
  return useQuery({
    queryKey: ["crm-lifecycle"],
    queryFn: () => api.get<LifecyclePipeline>("/v1/crm/lifecycle"),
  });
}

/** GET /v1/crm/opportunities — the advisor's book-wide OPEN opportunities, masked +
 *  server-ordered, cursor-paginated ("Daha fazla"). No retry — empty is a state. */
export function useOpportunities(pageSize = 20) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: ["crm-opportunities", pageSize],
    initialPageParam: null as string | null,
    retry: false,
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams({ limit: String(pageSize) });
      if (pageParam) p.set("cursor", pageParam);
      return api.get<Page<OpportunityRow>>(`/v1/crm/opportunities?${p.toString()}`);
    },
    getNextPageParam: (last) => (last?.hasMore ? last.nextCursor : undefined),
  });
}

/** POST /v1/crm/opportunities/:id/snooze — DEFER an opportunity out of the list
 *  until a date (CR-7). Refreshes the book-wide opportunity list. */
export function useSnoozeOpportunity() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; until: string; reason?: string }) =>
      api.post(`/v1/crm/opportunities/${input.id}/snooze`, {
        until: input.until,
        ...(input.reason ? { reason: input.reason } : {}),
      }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["crm-opportunities"] }),
  });
}

/** GET /v1/queue/suppressed — the book-wide HELD-BACK trail with the reason (#262).
 *  §1.3: a suppressed touch is visible, not merely absent. */
export function useSuppressed() {
  const api = useApi();
  return useQuery({
    queryKey: ["queue-suppressed"],
    retry: false,
    queryFn: () => api.get<SuppressedItem[]>("/v1/queue/suppressed"),
  });
}

/** GET /v1/crm/segments — the system segment counts over the caller's book. */
export function useSystemSegments() {
  const api = useApi();
  return useQuery({
    queryKey: ["crm-system-segments"],
    queryFn: () => api.get<SegmentSummary[]>("/v1/crm/segments"),
  });
}

/** GET /v1/crm/saved-segments — the org's user-authored saved segments (CR-1b). */
export function useSavedSegments() {
  const api = useApi();
  return useQuery({
    queryKey: ["crm-saved-segments"],
    queryFn: () => api.get<SavedSegment[]>("/v1/crm/saved-segments"),
  });
}

interface SavedHouseholdsResult {
  count: number;
  data: SegmentHousehold[];
  hasMore: boolean;
}

/** GET /v1/crm/segments/:key/households — a system segment's own-scoped households. */
export function useSystemSegmentHouseholds(key: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ["crm-system-segment-households", key],
    enabled: key !== null,
    queryFn: () =>
      api.get<{ data: SegmentHousehold[]; hasMore: boolean }>(
        `/v1/crm/segments/${key}/households?limit=25`,
      ),
  });
}

/** GET /v1/crm/saved-segments/:id/households — a saved segment's own-scoped households. */
export function useSavedSegmentHouseholds(id: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ["crm-saved-segment-households", id],
    enabled: id !== null,
    queryFn: () =>
      api.get<SavedHouseholdsResult>(`/v1/crm/saved-segments/${id}/households?limit=25`),
  });
}

/** GET /v1/crm/tasks — the caller's own personal tasks (optionally by status). */
export function useTasks(status?: Task["status"]) {
  const api = useApi();
  return useQuery({
    queryKey: ["crm-tasks", status ?? "all"],
    queryFn: () => api.get<Task[]>(`/v1/crm/tasks${status ? `?status=${status}` : ""}`),
  });
}

/** POST /v1/crm/tasks — create a personal follow-up task; refreshes the list. */
export function useCreateTask() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { title: string; note?: string | null; dueAt?: string | null }) =>
      api.post<Task>("/v1/crm/tasks", input),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["crm-tasks"] }),
  });
}

/** PATCH /v1/crm/tasks/:id — advance status (OPEN → DONE / DISMISSED). */
export function useUpdateTask() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; status: "DONE" | "DISMISSED" }) =>
      api.patch<Task>(`/v1/crm/tasks/${input.id}`, { status: input.status }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["crm-tasks"] }),
  });
}
